<script lang="ts">
  import { onMount } from 'svelte';
  import AppShell from '../components/AppShell.svelte';
  import Icon from '../components/Icon.svelte';
  import { tableIconName } from '../lib/icons';
  import {
    fetchDataRecord,
    fetchPublicDataTable,
    updateDataRecord,
    deleteDataRecord,
    type ApiField,
    type ApiFieldGroup,
    type ApiRecord,
    type ApiTable,
  } from '../lib/data-tables-api';
  import InlineFieldEdit from '../lib/fields/InlineFieldEdit.svelte';
  import ReverseLinks from '../lib/fields/ReverseLinks.svelte';
  import EntityTimeline from '../components/EntityTimeline.svelte';
  import FunnelJourney from '../components/FunnelJourney.svelte';
  import { session } from '../lib/session';
  import { canViewField, type Role } from '../lib/roles';

  let { params }: { params: { slug?: string; id?: string } } = $props();
  const slug = $derived(params?.slug ?? '');
  const id = $derived(params?.id ?? '');

  let table = $state<ApiTable | null>(null);
  let record = $state<ApiRecord | null>(null);
  let allFields = $state<ApiField[]>([]);
  let allGroups = $state<ApiFieldGroup[]>([]);
  let loading = $state(true);
  let err = $state<string | null>(null);
  let errorMsg = $state<string | null>(null);
  let menuOpen = $state(false);

  const isAdmin = $derived($session?.role === 'ADMIN');

  onMount(async () => {
    if (!slug || !id) {
      err = 'Не вказано slug або id';
      loading = false;
      return;
    }
    try {
      const [t, r] = await Promise.all([fetchPublicDataTable(slug), fetchDataRecord(slug, id)]);
      table = t;
      record = r;
      allFields = t.fields ?? [];
      allGroups = t.groups ?? [];
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  });

  /** Primary field для title (наприклад, "name" → "Acme Corp"). */
  const primaryField = $derived(allFields.find((f) => f.primary) ?? null);
  const title = $derived.by(() => {
    if (!record || !primaryField) return id.slice(0, 8);
    const v = record.values[primaryField.fieldKey];
    return v != null && v !== '' ? String(v) : id.slice(0, 8);
  });

  /** Згруповані поля для рендеру — sorted by group.position, fields.position.
   *  Поля без groupName → "Додаткові" внизу. Hidden — пропускаються. */
  const currentRole = $derived(($session?.role ?? null) as Role | null);

  const sections = $derived.by<{ groupName: string; fields: ApiField[] }[]>(() => {
    if (allFields.length === 0) return [];
    const map = new Map<string, ApiField[]>();
    for (const f of allFields) {
      if (f.hidden) continue;
      if (!canViewField(currentRole, f.min_view_role)) continue;
      const groupName = f.groupName || 'Додаткові';
      const arr = map.get(groupName) ?? [];
      arr.push(f);
      map.set(groupName, arr);
    }
    for (const [, arr] of map) {
      arr.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    }
    // Order: спочатку CustomFieldGroup за position, потім "Додаткові" останнім.
    const customPos = new Map<string, number>();
    for (const g of allGroups) customPos.set(g.name, g.position);
    const named = Array.from(map.entries())
      .filter(([k]) => k !== 'Додаткові')
      .sort(([a], [b]) => {
        const pa = customPos.get(a) ?? 9999;
        const pb = customPos.get(b) ?? 9999;
        if (pa !== pb) return pa - pb;
        return a.localeCompare(b);
      });
    const result = named.map(([groupName, fields]) => ({ groupName, fields }));
    if (map.has('Додаткові')) {
      result.push({ groupName: 'Додаткові', fields: map.get('Додаткові')! });
    }
    return result;
  });

  async function saveField(fieldKey: string, value: unknown): Promise<void> {
    if (!record) return;
    const v = value === '' || value === undefined ? null : value;
    const nextValues = { ...record.values, [fieldKey]: v };
    if (v === null) delete nextValues[fieldKey];
    await updateDataRecord(slug, id, nextValues);
    record = { ...record, values: nextValues };
  }

  async function onDelete(): Promise<void> {
    if (!confirm('Видалити цей запис? Цю дію не можна скасувати.')) return;
    try {
      await deleteDataRecord(slug, id);
      window.location.hash = `#/tables/${slug}`;
    } catch (e) {
      errorMsg = e instanceof Error ? e.message : String(e);
    }
  }
</script>

<AppShell active="custom-tables">
  <div class="mx-auto max-w-[1280px]">
    <div class="text-xs text-[var(--color-text-muted)] mb-2">
      <a href="#/tables/{slug}" class="text-[var(--color-pu)] no-underline hover:underline">
        {table?.name ?? slug}
      </a> ›
      <span>{title}</span>
    </div>

    {#if loading}
      <div class="py-20 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
    {:else if err}
      <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
    {:else if record && table}
      <!-- Header card -->
      <div class="mb-5 flex items-start gap-4 rounded-xl border border-[var(--color-border-3)] bg-gradient-to-b from-[var(--color-bg-3)] to-white p-5">
        <div
          class="grid h-16 w-16 shrink-0 place-items-center rounded-2xl"
          style="background: {table.color}20; color: {table.color};"
        >
          <Icon name={tableIconName(table.icon)} size={30} />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2.5 mb-1 flex-wrap">
            <span class="text-lg font-bold">{title}</span>
            <code class="rounded bg-[var(--color-bg-2)] px-1.5 py-0.5 text-[10.5px] text-[var(--color-text-muted)]">r-{record.id.slice(0, 8)}</code>
          </div>
          <p class="text-[12px] text-[var(--color-text-muted)]">{table.name}</p>
        </div>
        {#if isAdmin}
          <div class="relative">
            <button
              type="button"
              onclick={() => (menuOpen = !menuOpen)}
              class="grid h-9 w-9 place-items-center rounded-md border bg-white transition-colors
                {menuOpen ? 'border-[var(--color-pu)] text-[var(--color-pu-3)]' : 'border-[var(--color-border-3)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)]'}"
              title="Дії"
              aria-label="Дії"
            >
              <Icon name="more" size={18} />
            </button>
            {#if menuOpen}
              <div class="fixed inset-0 z-30" role="presentation" onclick={() => (menuOpen = false)}></div>
              <div
                class="absolute right-0 top-full mt-1.5 z-40 w-44 rounded-xl border border-[var(--color-border-3)] bg-white shadow-xl overflow-hidden py-1"
                role="menu"
                onclick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onclick={() => { menuOpen = false; void onDelete(); }}
                  class="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] font-medium text-red-700 hover:bg-red-50"
                >
                  <Icon name="delete" size={14} /> Видалити
                </button>
              </div>
            {/if}
          </div>
        {/if}
      </div>

      <!-- Body: schema-driven sidebar (без правого main column поки що — для custom
           таблиць тут немає Timeline/Аналітики). -->
      <div class="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-5 items-start">
        <aside class="rounded-xl border border-[var(--color-border-3)] bg-white">
          <div class="px-4 py-3 border-b border-[var(--color-border-3)] flex items-center justify-between">
            <span class="text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)]">Деталі</span>
            <a
              href="#/settings/data-structure/tables/{slug}"
              class="text-[10px] text-[var(--color-pu-3)] hover:underline"
              title="Налаштувати поля"
            >налаштувати</a>
          </div>

          {#each sections as section, sIdx (section.groupName)}
            <div class="px-4 py-3 {sIdx === 0 ? '' : 'border-t border-[var(--color-border-3)]'}">
              <div class="text-[9.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)] mb-2">
                {section.groupName}
              </div>
              {#each section.fields as f (f.id)}
                <InlineFieldEdit
                  field={f}
                  value={record.values[f.fieldKey]}
                  onSave={(v) => saveField(f.fieldKey, v)}
                  readOnly={!isAdmin}
                  record={record.values}
                />
              {/each}
            </div>
          {/each}

          <!-- Зворотні зв'язки — окрема група знизу полів. -->
          <div class="px-4 py-3 border-t border-[var(--color-border-3)]">
            <ReverseLinks sourceSlug={slug} sourceId={id} />
          </div>

          {#if errorMsg}
            <div class="mx-4 mb-3 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-[10.5px] text-red-700">{errorMsg}</div>
          {/if}
        </aside>

        <!-- Right area: активність + інформаційний блок. -->
        <main class="space-y-4 min-w-0">
          <FunnelJourney entityType={slug.toUpperCase()} entityId={id} />
          <EntityTimeline entityType={slug.toUpperCase()} entityId={id} />

          <div class="rounded-xl border border-[var(--color-border-3)] bg-white p-5">
            <div class="text-[13px] font-bold mb-2">Інформація</div>
            <div class="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
              <div class="flex justify-between">
                <span class="text-[var(--color-text-muted)]">Створено</span>
                <span class="tabular-nums">{new Date(record.createdAt).toLocaleString('uk-UA', { dateStyle: 'short', timeStyle: 'short' })}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-[var(--color-text-muted)]">Оновлено</span>
                <span class="tabular-nums">{new Date(record.updatedAt).toLocaleString('uk-UA', { dateStyle: 'short', timeStyle: 'short' })}</span>
              </div>
              <div class="flex justify-between col-span-2">
                <span class="text-[var(--color-text-muted)]">Запис ID</span>
                <code class="font-mono text-[10.5px]">{record.id}</code>
              </div>
            </div>
          </div>
        </main>
      </div>
    {/if}
  </div>
</AppShell>
